import { ChangeEvent, FocusEvent, KeyboardEvent } from 'react';
import { FormControl, FormLabel, FormErrorMessage } from '@chakra-ui/react';
import { Field, FieldProps, useFormikContext } from 'formik';

export type InjectedInputProps = {
   id: string;
   name: string;
   placeholder?: string;
   value: any;
   onChange(e: ChangeEvent<any>): void;
   onBlur(e: FocusEvent<any>): void;
};

type ControlProps = {
   name: string;
   label: string;
   placeholder?: string;
   validate?(value: any): any;
   onKeyDown?(e: KeyboardEvent<any>): void;
};

/** 
 * withFormControl
 * Helper for creating FormControl components.
 * @param InputComponent - A component to wrap with standard markup and behavior for labels
      and validation.
 * @returns FormControl - A component which accepts standard props: name | label | placeholder | validation, 
      and renders an form control containing the InputComponent

 * @usage Simple -
      export const TextFormControl = withFormControl( (props) => {
         return <Input type="text" {...props} />;
      });

 * @usage Extending Control Props - 
      type SwitchControlProps = InjectedInputProps & {
         onHelperText?: string;
         offHelperText?: string;
      };

      export const SwitchFormControl = withFormControl(
         ({ value, onHelperText, offHelperText, onBlur, ...props }: SwitchControlProps) => {
            return (
               <Flex align="center">
                  <Switch {...props} value={value} />
                  <FormHelperText mt={0} ml={2}>
                     {value ? onHelperText : offHelperText}
                  </FormHelperText>
               </Flex>
            );
         }
      );
*/
export function withFormControl<P>(InputComponent: React.ComponentType<P & InjectedInputProps>) {
   return function Control({ name, label, placeholder, validate, ...props }: ControlProps & P) {
      const { values, handleChange, handleBlur } = useFormikContext();
      return (
         <Field name={name.toString()} validate={validate}>
            {({ meta }: FieldProps) => (
               <FormControl my={5} isInvalid={meta.touched && typeof meta.error !== 'undefined'}>
                  <FormLabel htmlFor={name.toString()} id={`field-${name.toString()}-label`}>
                     {label}
                  </FormLabel>
                  <InputComponent
                     {...(props as any)}
                     id={name.toString()}
                     name={name.toString()}
                     placeholder={placeholder}
                     value={(values as any)[name]}
                     onChange={handleChange}
                     onBlur={handleBlur}
                  />
                  <FormErrorMessage>{meta.error}</FormErrorMessage>
               </FormControl>
            )}
         </Field>
      );
   };
}
